import { type OptionsType, deleteCookie } from 'cookies-next'
import type { User as AuthUser, UserInfo } from 'firebase/auth'
import { usePostHog } from 'posthog-js/react'
import { useMemo } from 'react'
import { useAuthState } from 'react-firebase-hooks/auth'
import {
  DUPE_AUTH_USER_ID_KEY,
  DUPE_FLAGS_COOKIE_KEY,
  TEMP_USER_ID_KEY,
} from '~/src/lib/constants'
import auth from '~/src/lib/firebase/firebaseAuth'

export interface AuthUserWithToken extends AuthUser {
  accessToken?: string
}

type Response = {
  authUser?: AuthUserWithToken | null | undefined
  isAdmin: boolean
  loading: boolean
  error: Error | undefined
  signOut: () => Promise<void>
}

function getPropertyFromProviderData(
  property: keyof UserInfo,
  authUser: AuthUserWithToken
) {
  return (
    authUser?.[property] ||
    (authUser?.providerData || []).find((pd) => pd[property])?.[property] ||
    null
  )
}

const ADMIN_EMAILS = [
  'i8ramin@gmail.com',
  'arnabg@gmail.com',
  'mariano.selvaggi81@gmail.com',
  'mariano.selvaggi.81@gmail.com',
  'gabilhartman@gmail.com',
]

const useAuthUser = (): Response => {
  const posthog = usePostHog()
  const [authUser, loading, error] = useAuthState(auth)
  const isAdmin =
    authUser?.email?.endsWith('@addtocarrot.com') ||
    authUser?.email?.endsWith('@carrot.link') ||
    authUser?.email?.endsWith('@dupe.com') ||
    ADMIN_EMAILS.includes(authUser?.email || '') ||
    false

  const enhancedAuthUser = useMemo(
    () =>
      authUser
        ? ({
            ...authUser,
            displayName: getPropertyFromProviderData('displayName', authUser),
            photoURL: getPropertyFromProviderData('photoURL', authUser),
          } as AuthUserWithToken)
        : undefined,
    [authUser]
  )

  const signOut = async () => {
    const cookieOptions: OptionsType = {
      path: '/',
    }

    if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'production') {
      cookieOptions.domain = '.dupe.com'
    }

    try {
      deleteCookie(DUPE_AUTH_USER_ID_KEY, cookieOptions)
      deleteCookie(TEMP_USER_ID_KEY, cookieOptions)
      deleteCookie(DUPE_FLAGS_COOKIE_KEY, cookieOptions)
      window.localStorage.removeItem(TEMP_USER_ID_KEY)
    } catch (error) {
      console.error('Error signing out:', error)
    }

    // Reset posthog (https://posthog.com/docs/libraries/js/features#resetting-a-user)
    posthog.reset()

    await auth.signOut()
  }

  return {
    authUser: enhancedAuthUser,
    isAdmin,
    loading,
    error,
    signOut,
  }
}

export default useAuthUser
